<template>
  <div class="sales-summary">
    <div class="ottopay-import">
      <div class="title">Import OttoPay Report</div>
      <p class="ottopay-help">
        Upload the CSV exported from OttoPay. The preview does not change any orders.
        Only unique, safe matches can be updated.
      </p>
      <div class="ottopay-controls">
        <input type="file" accept=".csv,text/csv" @change="handleOttopayFile" />
        <b-button
          variant="primary"
          :disabled="!ottopayRows.length || ottopayLoading"
          @click="previewOttopayReport"
        >
          {{ ottopayLoading ? 'Checking...' : 'Check report' }}
        </b-button>
      </div>
      <p v-if="ottopayFileName" class="ottopay-file">
        {{ ottopayFileName }} — {{ ottopayRows.length }} rows
      </p>
      <b-alert v-if="ottopayError" show variant="danger">{{ ottopayError }}</b-alert>

      <div v-if="ottopayResult" class="ottopay-result">
        <div class="ottopay-summary">
          <button type="button" :class="{ active: ottopayFilter === 'READY_TO_UPDATE' }" :aria-pressed="ottopayFilter === 'READY_TO_UPDATE'" @click="toggleOttopayFilter('READY_TO_UPDATE')"><strong>{{ ottopayResult.summary.readyToUpdate }}</strong><span>Ready to update</span></button>
          <button type="button" :class="{ active: ottopayFilter === 'ALREADY_COMPLETED' }" :aria-pressed="ottopayFilter === 'ALREADY_COMPLETED'" @click="toggleOttopayFilter('ALREADY_COMPLETED')"><strong>{{ ottopayResult.summary.alreadyCompleted }}</strong><span>Already completed</span></button>
          <button type="button" :class="{ active: ottopayFilter === 'UNMATCHED' }" :aria-pressed="ottopayFilter === 'UNMATCHED'" @click="toggleOttopayFilter('UNMATCHED')"><strong>{{ ottopayResult.summary.unmatched }}</strong><span>Unmatched</span></button>
          <button type="button" :class="{ active: ottopayFilter === 'DOUBLE_CHARGE' }" :aria-pressed="ottopayFilter === 'DOUBLE_CHARGE'" @click="toggleOttopayFilter('DOUBLE_CHARGE')"><strong>{{ ottopayResult.summary.doubleCharges }}</strong><span>Possible double charges</span></button>
          <button type="button" :class="{ active: ottopayFilter === 'NEEDS_REVIEW' }" :aria-pressed="ottopayFilter === 'NEEDS_REVIEW'" @click="toggleOttopayFilter('NEEDS_REVIEW')"><strong>{{ ottopayIssueCount }}</strong><span>Needs review</span></button>
          <button v-if="ottopayResult.applied" type="button" :class="{ active: ottopayFilter === 'UPDATED' }" :aria-pressed="ottopayFilter === 'UPDATED'" @click="toggleOttopayFilter('UPDATED')"><strong>{{ ottopayResult.summary.updated }}</strong><span>Updated</span></button>
        </div>

        <p v-if="ottopayFilter !== 'ALL'" class="ottopay-filter-status">
          Showing {{ filteredOttopayRows.length }} of {{ ottopayResult.rows.length }} transactions.
          <button type="button" @click="ottopayFilter = 'ALL'">Show all</button>
        </p>

        <b-alert
          v-if="ottopayResult.summary.duplicateRows"
          show
          variant="info"
        >
          {{ ottopayResult.summary.duplicateRows }} duplicate rows were ignored.
        </b-alert>

        <b-button
          v-if="!ottopayResult.applied && ottopayResult.summary.readyToUpdate"
          variant="success"
          :disabled="ottopayLoading"
          @click="confirmOttopayUpdates"
        >
          Update {{ ottopayResult.summary.readyToUpdate }} orders
        </b-button>

        <div class="ottopay-table-wrap">
          <table class="table table-sm table-bordered ottopay-table">
            <thead>
              <tr>
                <th>OttoPay transaction</th>
                <th>Store / amount</th>
                <th>Yobo order</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredOttopayRows" :key="row.rowKey">
                <td>
                  <strong>{{ row.ottopayOrderId || '-' }}</strong><br />
                  Ref: {{ row.referenceNumber || '-' }}<br />
                  <small>{{ row.transactionDate }}</small>
                </td>
                <td>
                  {{ row.storeName }}<br />
                  {{ row.paymentType || row.paymentMethod }} — {{ formatAmount(row.grossAmount) }}
                </td>
                <td>
                  <strong>{{ row.orderId || '-' }}</strong><br />
                  <small>{{ row.currentStatus || '-' }}</small>
                </td>
                <td>
                  <strong>{{ ottopayResultLabel(row) }}</strong><br />
                  <small>{{ row.message }}</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <h4 class="setTittle">
      <div class="title">Input Receipt Number</div>
      <b-form-textarea
        v-model="orderId"
        style="margin: 20px 0; height: 100px;"
        placeholder="Input Receipt Number"
      ></b-form-textarea>
      <div class="title">Change Order Status</div>
      <b-form-select v-model="selectedOrderStatus" :options="orderStatus"></b-form-select>
      <div style="margin: 20px 0;"><b-button variant="success" @click="handleSubmit(1)">Submit</b-button></div>
      <div class="title" style="margin-top: 20px">Change Payment Method</div>
      <b-form-select v-model="selectedPaymentMethod" :options="paymentMethods"></b-form-select>
      <b-button style="margin: 20px 0;" variant="success" @click="handleSubmit(2)">Submit</b-button>
      <p
        style="margin-top: 20px; font-size: 16px;"
      >You can wrap lines or separate order numbers with commas, like</p>
      <div style="font-size: 16px;">
        1.
        <br />
        <span>
          0686840714DI220007,
          0686840714DI220006,
          0686840714DI220014,
          0686840714DI220013
        </span>
        <br />
        <br />
        2.
        <br />
        <span>
          0686840714DI220007
          <br />0686840714DI220006
          <br />0686840714DI220014
          <br />0686840714DI220013
          <br />
        </span>
        <br />3.
        <br />
        <span>
          0686840714DI220007,
          <br />0686840714DI220006,
          <br />0686840714DI220014,
          <br />0686840714DI220013
          <br />
        </span>
      </div>
    </h4>
  </div>
</template>

<script>
import api from '@/api';
import constants from '@/utils/constants';
import { matchesOttopayFilter, parseOttopayReport } from '@/utils/ottopayReport';
export default {
  data() {
    return {
      orderStatus: [
        'COMPLETED',
        'CANCELLED',
        'INIT',
        'SUBMITTED',
        'PAYMENT_SUCCESS',
        'EXPIRED',
        'TRANSFERRED',
        'CONFIRMED',
        'PREPARED',
        'ACCEPTED',
        'READY_PICKUP',
        'ON_DELIVERY',
        'BAD',
        'CONFIRM'
      ],
      paymentMethods: [
        'CIMBQR Static',
        'Shopeefood',
        'Traveloka',
        'VoucherASRI',
        'Gojek',
        'Grabfood',
        'QR - BCA',
      ],
      selectedOrderStatus: 'COMPLETED',
      orderId: '',
      selectedPaymentMethod: 'CIMBQR Static',
      ottopayFileName: '',
      ottopayRows: [],
      ottopayResult: null,
      ottopayFilter: 'ALL',
      ottopayLoading: false,
      ottopayError: ''
    };
  },
  computed: {
    ottopayIssueCount() {
      if (!this.ottopayResult) return 0;
      const summary = this.ottopayResult.summary;
      return summary.ambiguous + summary.conflicts + summary.notEligible + summary.updateFailed + summary.doubleCharges;
    },
    filteredOttopayRows() {
      if (!this.ottopayResult) return [];
      return this.ottopayResult.rows.filter(row => matchesOttopayFilter(row, this.ottopayFilter));
    }
  },
  methods: {
    handleOttopayFile(event) {
      const file = event.target.files && event.target.files[0];
      this.ottopayRows = [];
      this.ottopayResult = null;
      this.ottopayFilter = 'ALL';
      this.ottopayError = '';
      this.ottopayFileName = file ? file.name : '';
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        this.ottopayError = 'The CSV file must be smaller than 5 MB.';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          this.ottopayRows = parseOttopayReport(String(reader.result || ''));
        } catch (err) {
          this.ottopayError = err.message || 'Unable to read the OttoPay CSV.';
        }
      };
      reader.onerror = () => {
        this.ottopayError = 'Unable to read the OttoPay CSV.';
      };
      reader.readAsText(file);
    },
    previewOttopayReport() {
      return this.runOttopayReport(false);
    },
    confirmOttopayUpdates() {
      const count = this.ottopayResult.summary.readyToUpdate;
      this.$swal({
        title: `Update ${count} orders?`,
        text: 'Each matched order will be moved through PAYMENT_SUCCESS to COMPLETED.',
        icon: 'warning',
        buttons: true
      }).then(confirmed => {
        if (confirmed) this.runOttopayReport(true);
      });
    },
    async runOttopayReport(apply) {
      this.ottopayLoading = true;
      this.ottopayError = '';
      try {
        const res = await api.ReconcileOttopayReport({ rows: this.ottopayRows, apply });
        if (!res.data.data || !res.data.data.summary) {
          throw new Error(res.data.invalidMessage || 'The report could not be checked.');
        }
        this.ottopayResult = res.data.data;
        this.ottopayFilter = 'ALL';
        if (apply && this.ottopayResult.summary.updated) {
          this.$swal(
            constants.UPPER_SUCCESS,
            `${this.ottopayResult.summary.updated} orders updated to COMPLETED`,
            constants.STATUS_SUCCESS
          );
        }
      } catch (err) {
        this.ottopayError =
          (err.response && err.response.data &&
            (err.response.data.invalidMessage || err.response.data.message)) ||
          err.message ||
          'Unable to reconcile the OttoPay report.';
      }
      this.ottopayLoading = false;
    },
    formatAmount(value) {
      return `Rp ${Number(value || 0).toLocaleString('id-ID')}`;
    },
    toggleOttopayFilter(filter) {
      this.ottopayFilter = this.ottopayFilter === filter ? 'ALL' : filter;
    },
    ottopayResultLabel(row) {
      if (row.action === 'UPDATED') return 'Updated';
      if (row.action === 'READY_TO_UPDATE') return 'Ready';
      if (row.action === 'ALREADY_COMPLETED') return 'Already completed';
      if (row.action === 'UPDATE_FAILED') return 'Update failed';
      if (row.matchStatus === 'AMBIGUOUS') return 'Ambiguous';
      if (row.matchStatus === 'CONFLICT') return 'Conflict';
      if (row.matchStatus === 'DOUBLE_CHARGE') return 'Possible double charge';
      if (row.matchStatus === 'UNMATCHED') return 'Unmatched';
      return 'Not eligible';
    },
    handleSubmit(type) {
      if (!this.orderId) {
        return this.$swal(
          constants.SOMETHING_WROING,
          'Please input receipt number',
          constants.STATUS_ERROR
        );
      }
      const payload = {
        orderId: this.orderId
          .replace(/\n/g, ',')
          .replace(/\s*/g, '')
          .split(',')
          .filter(item => !!item)
          .join(','),
        order_status: this.selectedOrderStatus
      };
      if (type === 2) {
        delete payload.order_status
        payload.payment_method = this.selectedPaymentMethod
      }
      api[type === 1 ? 'ChangeOrderStatus' : 'ChangeOrderPaymentMethod'](payload)
        .then(res => {
          const { code, message } = res.data;
          if (code === constants.STATUS_200_STRING) {
            this.orderId = '';
            this.$swal(
              constants.UPPER_SUCCESS,
              'Order status already changed',
              constants.STATUS_SUCCESS
            );
          } else {
            this.$swal(
              constants.SOMETHING_WROING,
              message,
              constants.STATUS_ERROR
            );
          }
        })
        .catch(err => {
          this.$swal(
            constants.SOMETHING_WROING,
            err.response.data.message,
            constants.STATUS_ERROR
          );
        });
    }
  }
};
</script>

<style scoped>
.ottopay-import {
  margin-bottom: 30px;
  padding: 20px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
}
.ottopay-help,
.ottopay-file {
  margin: 8px 0 14px;
  font-size: 14px;
}
.ottopay-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.ottopay-summary {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 20px 0;
}
.ottopay-summary > button {
  min-width: 130px;
  padding: 12px;
  text-align: center;
  background: #f7f7f7;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
}
.ottopay-summary > button:hover,
.ottopay-summary > button:focus,
.ottopay-summary > button.active {
  border-color: #f5003c;
  outline: none;
}
.ottopay-summary > button.active {
  background: #fff0f4;
}
.ottopay-summary strong,
.ottopay-summary span {
  display: block;
}
.ottopay-summary strong {
  font-size: 22px;
}
.ottopay-filter-status {
  margin: -8px 0 14px;
}
.ottopay-filter-status button {
  padding: 0;
  color: #f5003c;
  background: none;
  border: 0;
  cursor: pointer;
  text-decoration: underline;
}
.ottopay-table-wrap {
  max-height: 520px;
  margin-top: 18px;
  overflow: auto;
}
.ottopay-table {
  min-width: 850px;
  font-size: 13px;
}
.ottopay-table th {
  position: sticky;
  top: 0;
  background: #f7f7f7;
}
</style>
